import { useEffect, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid2';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import {
  Controller,
  useFormState,
  useFieldArray,
  useFormContext,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { sentenceCase } from '../../../../utils/utils';
import Text from '../../../elements/text/Text';
import TextInput from '../../../elements/text-input/TextInput';

const CrawlStep4 = ({ instances, getCollections, getUniqueKey }) => {
  const { t } = useTranslation();
  const { control, setValue, trigger, watch } = useFormContext();
  const { errors } = useFormState({ control });
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'crawlArgs',
  });

  const [collections, setCollections] = useState([]);

  const { instance, collection } = watch();

  const fetchCollections = async () => {
    const foundInstance = instances.find((inst) => inst.id === instance);
    const collectRes = await getCollections(foundInstance);
    setCollections(collectRes);
  };

  useEffect(() => {
    if (!instance) return;
    fetchCollections();
  }, [instance]);

  const fetchUniqueKey = async () => {
    const uniqueKey = await getUniqueKey(instance, collection);
    setValue('uniqueKey', uniqueKey, { shouldValidate: true });
  };

  useEffect(() => {
    if (!instance || !collection) return;
    fetchUniqueKey();
  }, [instance, collection]);

  const numOrBlank = (val) => /[0-9]/.test(val) || val === '';

  return (
    <Box>
      <section>
        <Box sx={{ mb: 2 }}>
          <Text caption style={{ color: 'gray' }}>
            {t('asteriskRequired', { ns: 'fields' })}
          </Text>
        </Box>
        <Grid container spacing={2} sx={{ marginBottom: '1em' }}>
          <Grid
            size={{
              xs: 12,
              sm: 4,
            }}
          >
            <FormControl
              variant="outlined"
              size="small"
              fullWidth
              error={!!errors?.instance}
            >
              <InputLabel>
                {`${t('instanceName', { ns: 'fields' })} *`}
              </InputLabel>
              <Controller
                name="instance"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <Select
                    {...field}
                    label={`${t('instanceName', { ns: 'fields' })} *`}
                    inputProps={{ 'aria-label': 'instance select' }}
                  >
                    {isEmpty(instances) ? (
                      <MenuItem value="">
                        {sentenceCase(
                          t('noFound', {
                            ns: 'descriptions',
                            text: t('instance', { ns: 'titles' }),
                          })
                        )}
                      </MenuItem>
                    ) : (
                      instances.map((inst) => (
                        <MenuItem key={inst.id} value={inst.id}>
                          {inst.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                )}
              />
              <FormHelperText>{errors?.instance?.message}</FormHelperText>
            </FormControl>
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 4,
            }}
          >
            <FormControl
              variant="outlined"
              size="small"
              fullWidth
              error={!!errors?.collection}
            >
              <InputLabel>
                {`${t('collectionName', { ns: 'fields' })} *`}
              </InputLabel>
              <Controller
                name="collection"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <Select
                    {...field}
                    label={`${t('collectionName', { ns: 'fields' })} *`}
                    inputProps={{ 'aria-label': 'collection select' }}
                  >
                    {isEmpty(collections) ? (
                      <MenuItem value="">
                        {sentenceCase(
                          t('noFound', {
                            ns: 'descriptions',
                            text: t('collections', { ns: 'titles' }),
                          })
                        )}
                      </MenuItem>
                    ) : (
                      collections.map((collect) => (
                        <MenuItem key={collect} value={collect}>
                          {collect}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                )}
              />
              <FormHelperText>{errors?.collection?.message}</FormHelperText>
            </FormControl>
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 4,
            }}
          >
            <TextInput
              name="uniqueKey"
              label={`${t('uniqueKey', { ns: 'fields' })} *`}
              control={control}
              errors={errors}
              disabled
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          {['updateHandler', 'removeHandler', 'statusHandler'].map(
            (handler) => (
              <Grid
                key={handler}
                size={{
                  xs: 12,
                  sm: 4,
                }}
              >
                <TextInput
                  name={handler}
                  label={`${t(handler, { ns: 'fields' })} *`}
                  control={control}
                  errors={errors}
                />
              </Grid>
            )
          )}
        </Grid>
        <Box sx={{ mt: 1 }}>
          <Box sx={{ mb: 1 }}>
            <Text title>{t('commitSettings', { ns: 'sectionTitles' })}</Text>
          </Box>
          <Grid container spacing={2}>
            <Grid
              size={{
                xs: 12,
                sm: 4,
              }}
            >
              <Controller
                name="commitTime"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('commitTime', { ns: 'fields' })}
                    variant="outlined"
                    inputProps={{ 'aria-label': 'commitTime' }}
                    fullWidth
                    multiline
                    size="small"
                    onChange={(e) => {
                      numOrBlank(e.target.value) &&
                        field.onChange(e.target.value);
                      trigger(['commitTime', 'commitAfterJob']);
                    }}
                    error={!!errors?.commitTime}
                    helperText={errors?.commitTime?.message}
                  />
                )}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 8,
              }}
            >
              <FormControl error={!!errors.commitAfterJob}>
                <FormControlLabel
                  label={t('commitAfterJob', { ns: 'fields' })}
                  control={
                    <Controller
                      name="commitAfterJob"
                      control={control}
                      defaultValue={false}
                      render={({ field }) => (
                        <Checkbox
                          {...field}
                          inputProps={{
                            'aria-label': 'commitAfterJob',
                          }}
                          checked={field.value}
                          onChange={(e) => {
                            field.onChange(e.target.checked);
                            trigger(['commitTime', 'commitAfterJob']);
                          }}
                        />
                      )}
                    />
                  }
                />
                <FormHelperText>
                  {errors.commitAfterJob?.message}
                </FormHelperText>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
        <Box>
          <Box sx={{ mb: 1 }}>
            <Text title>{t('argumentSettings', { ns: 'sectionTitles' })}</Text>
          </Box>
          {fields.map((item, index) => (
            <Grid container spacing={2} key={item.id}>
              <Grid
                size={{
                  xs: 12,
                  sm: 4,
                }}
              >
                <Controller
                  name={`crawlArgs[${index}].name`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      variant="outlined"
                      label={t('argName', { ns: 'fields' })}
                      size="small"
                      error={!!errors?.crawlArgs?.[index]?.name}
                      helperText={errors?.crawlArgs?.[index]?.name?.message}
                      inputProps={{
                        'aria-label': `crawlArgs[${index}].name`,
                      }}
                      onChange={(e) => {
                        field.onChange(e.target.value);
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 4,
                }}
              >
                <Controller
                  name={`crawlArgs[${index}].val`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      variant="outlined"
                      label={t('argValue', { ns: 'fields' })}
                      size="small"
                      error={!!errors?.crawlArgs?.[index]?.val}
                      helperText={errors?.crawlArgs?.[index]?.val?.message}
                      inputProps={{ 'aria-label': `crawlArgs[${index}].val` }}
                      onChange={(e) => {
                        field.onChange(e.target.value);
                      }}
                    />
                  )}
                />
              </Grid>
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                {index > 0 && (
                  <IconButton
                    style={{ marginRight: '0.5em', marginTop: 14 }}
                    onClick={() => remove(index)}
                    size="small"
                    aria-label={`remove-${index}`}
                  >
                    <RemoveIcon />
                  </IconButton>
                )}
                <IconButton
                  style={{ marginTop: 14 }}
                  onClick={() => append({ name: '', val: '' })}
                  size="small"
                  aria-label={`add-${index}`}
                >
                  <AddIcon />
                </IconButton>
              </Box>
            </Grid>
          ))}
        </Box>
      </section>
    </Box>
  );
};

CrawlStep4.propTypes = {
  instances: PropTypes.array.isRequired,
  getCollections: PropTypes.func.isRequired,
  getUniqueKey: PropTypes.func,
};

export default CrawlStep4;
